<template>
  <div class="background flex h-full flex-col">
    <LoadingScreen v-if="isLoading" :message="!authLoaded ? 'Gebruiker zoeken' : undefined" />
    <SynchronisationOverlay />

    <div class="mx-auto flex w-full grow overflow-y-auto transition-all duration-300 ease-in-out">
      <div class="w-full">
        <router-view v-slot="{ Component }">
          <keep-alive include="LayoutColumnsView">
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>
    </div>

    <DialogViewer />
    <AlertViewer class="z-40" />
    <Toast />
    <PwaUpdatePrompt v-if="!Capacitor.isNativePlatform()" />
    <CapgoUpdatePrompt v-if="Capacitor.isNativePlatform()" />
    <NativeStoreUpdatePrompt v-if="Capacitor.isNativePlatform()" />
    <ModelFormViewer />
    <MentionDetailModal />

    <div class="z-30">
      <DraggableWindow
        v-for="window in windows"
        :key="window.id"
        @close="onCloseWindow"
        @loaded="onLoadedWindow"
        :id="window.id"
      >
        <component :is="getWindowChildComponent(window.type)" v-bind="window.props" />
      </DraggableWindow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from "vue"
import { storeToRefs } from "pinia"
import DialogViewer from "@/components/misc/DialogViewer.vue"
import { useAuthStore } from "@/store/auth.store"
import AlertViewer from "@/components/alerts/AlertViewer.vue"
import DraggableWindow from "@/components/misc/DraggableWindow.vue"
import { useWindowStore } from "./store/windows.store"
import { WindowTypes } from "@/interfaces/component/window.interface"
import FileViewer from "@/components/file/FileViewer.vue"
import LoadingScreen from "@/components/loading/LoadingScreen.vue"
import SynchronisationOverlay from "@/components/synchronisation/SynchronisationOverlay.vue"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { isMobileMq } from "@/helper/app/device-helper"
import { type BeforeInstallPromptEvent } from "@/interfaces/html/before-install-prompt.interface"
import useControllers from "@/composables/controller/provide-controllers.ts"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import ModelFormViewer from "@/components/crud/ModelFormViewer.vue"
import MentionDetailModal from "@/components/mention/MentionDetailModal.vue"
import { useAlertStore } from "@/store/alert.store"
import { AlertTypes } from "./interfaces/component/alert.interface"
import { useOnline, useStorage } from "@vueuse/core"
import useAuthentication from "@/composables/auth/authentication"
import Toast from "primevue/toast"
import PwaUpdatePrompt from "@/components/app/PwaUpdatePrompt.vue"
import CapgoUpdatePrompt from "@/components/app/CapgoUpdatePrompt.vue"
import NativeStoreUpdatePrompt from "@/components/app/NativeStoreUpdatePrompt.vue"
import { Capacitor } from "@capacitor/core"
import { markPwaStartupComplete } from "@/services/pwa-update-service"

const alertStore = useAlertStore()
const authStore = useAuthStore()
const windowStore = useWindowStore()
const dialogStore = useDialogStore()
const layoutColumnStore = useLayoutColumnsStore()

const { activeUser } = storeToRefs(authStore)
const { windows } = storeToRefs(windowStore)
const { allColumnsLoaded } = storeToRefs(layoutColumnStore)

const authLoaded = ref(false)
const minLoadingTimePassed = ref(false)
let pwaStartupMarked = false

const isLoading = computed(() => {
  return (
    (!allColumnsLoaded.value && !!activeUser.value) ||
    !authLoaded.value ||
    !minLoadingTimePassed.value
  )
})

const controllers = useControllers()
const { provideControllers } = controllers
provideControllers()

// Pass the authController to the useAuthentication composable because the injection is not available at this level.
const authentication = useAuthentication(controllers.getAuthController())

onMounted(async () => {
  // Start a timer for minimum loading time
  setTimeout(() => {
    minLoadingTimePassed.value = true
  }, 2000)
  await authentication.initialize()
  authLoaded.value = true
})

watch(
  [authLoaded, minLoadingTimePassed],
  ([loaded, minTimePassed]) => {
    if (loaded && minTimePassed && !Capacitor.isNativePlatform() && !pwaStartupMarked) {
      pwaStartupMarked = true
      markPwaStartupComplete()
    }
  },
  { immediate: true }
)

function getWindowChildComponent(type: WindowTypes) {
  if (type === WindowTypes.IMAGE_VIEWER) {
    return FileViewer
  }
}

function onCloseWindow(id: number) {
  windowStore.closeWindow(id)
}

function onLoadedWindow(id: number) {
  windowStore.loadedWindow(id)
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
const showPrompt = ref(false)

const lastAppInstallPromptDate = useStorage("AppLastInstallPromptDate", "")

function shouldShowPrompt() {
  if (!lastAppInstallPromptDate.value) return true // Show prompt if it's never been shown before
  const threeMonthsAgo = new Date()
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
  return new Date(lastAppInstallPromptDate.value) < threeMonthsAgo
}

// Google Play: Prominent Disclosure and Consent Requirement: Missing Prominent Disclosure
watch(
  () => activeUser.value?.id,
  (userId) => {
    // Clerck test user ID
    if (userId === "eb0ea7fa-62d5-4149-9026-2111d4f94449") {
      dialogStore.currentDialog = {
        type: DialogTypes.OK,
        title: "Achtergrondlocatietoegang",
        body: "Peanuts gebruikt locatiegegevens om de planning goed te laten werken en klanten te informeren over hun bestelling, ook wanneer de app op de achtergrond draait of gesloten is. Privacy eerst: locatiegebruik is alleen actief tijdens je dienst en kan op elk moment worden uitgeschakeld in de instellingen."
      }
    }
  }
)

watch(allColumnsLoaded, (val) => {
  if (!val && showPrompt.value === true) {
    dialogStore.currentDialog = {
      type: DialogTypes.CONFIRM,
      title: "Installeer onze App",
      body: "Installeer de Peanuts App voor de optimale gebruikservaring!",
      onConfirm: () => installApp(),
      onCancel: () => {
        deferredPrompt = null
        lastAppInstallPromptDate.value = new Date().toISOString()
      },
      textConfirm: "Installeren",
      classConfirm: "bg-primary! text-on-primary!"
    }
  }
})

window.addEventListener("beforeinstallprompt", (e: BeforeInstallPromptEvent) => {
  e.preventDefault()
  // Only show the prompt every x months
  if (shouldShowPrompt()) {
    // Save the event and trigger it later.
    deferredPrompt = e
    // Open prompt on mobile (when done loading)
    if (isMobileMq()) {
      showPrompt.value = true
    }
  }
})

async function installApp() {
  if (deferredPrompt) {
    // Open the prompt
    deferredPrompt.prompt()
    // Find out whether the user confirmed the installation or not
    const userChoice = await deferredPrompt.userChoice
    // The deferredPrompt can only be used once.
    deferredPrompt = null
    // Handle choice
    if (userChoice.outcome === "accepted") {
      // Empty
    } else if (userChoice.outcome === "dismissed") {
      // Update to use reactive storage
      lastAppInstallPromptDate.value = new Date().toISOString()
    }
  }
}

// Reactive check for network and cookies
const cookieEnabled = ref(checkCookieEnabled())

function checkCookieEnabled() {
  try {
    document.cookie = "cookietest=1"
    const ret = document.cookie.indexOf("cookietest=") !== -1
    document.cookie = "cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT"
    return ret
  } catch (e) {
    console.log(e)
    return false
  }
}
const online = useOnline()

watch(
  online,
  (isOnline) => {
    if (!isOnline) {
      dialogStore.currentDialog = {
        type: DialogTypes.NO_BUTTON,
        title: "Netwerkfout",
        body: "Het lijkt erop dat je momenteel geen internetverbinding hebt. Controleer je verbinding en probeer het opnieuw.",
        lock: true
      }
      return
    }

    if (
      dialogStore.currentDialog?.type === DialogTypes.NO_BUTTON &&
      dialogStore.currentDialog.title === "Netwerkfout"
    ) {
      dialogStore.currentDialog = null
    }
  },
  { immediate: true }
)

watch(
  cookieEnabled,
  (enabled) => {
    if (!enabled) {
      alertStore.addAlert({
        type: AlertTypes.ERROR,
        title: "Opslagfout",
        body: "Het lijkt erop dat cookies zijn uitgeschakeld. Zet ze aan voor een optimale gebruikerservaring."
      })
    }
  },
  { immediate: true }
)
</script>

<style lang="scss">
.v-leave-active {
  transition: opacity 0.5s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
